import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users, CalendarCheck, TrendingUp, CalendarDays, Search, CheckCircle, XCircle,
  Clock, Plus, Star, Award, ClipboardCheck, Phone, Mail, Dumbbell, Bell, AlertTriangle, Eye, Zap, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { bestPhotoFor } from '@/lib/photo';
import { DigitalAthleteCard } from '@/components/DigitalAthleteCard';
import { getSportMatrix, AVAILABLE_SPORTS_LIST } from '@/lib/sportsMetrics';
// Phase 220 — Coach onboarding walkthrough player (silent if SA hasn't pinned any)
import SelfServiceWalkthroughSection from '@/components/dashboard/SelfServiceWalkthroughSection';
// Phase 264 — Attendance Blitz Deck for rapid one-tap session logging
import AttendanceBlitzDeck from '@/components/coach/AttendanceBlitzDeck';
// Phase 307 — Geofence ping + live status badge.
import useGeofencePing, { GeofenceStatusDot } from '@/hooks/useGeofencePing';
// Phase 313 — Live RSVP roster (auto-refreshes every 60s)
import LiveRsvpRoster from '@/components/coach/LiveRsvpRoster';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ProfilePic({ name, photoUrl, student, employee, size = 'h-8 w-8' }) {
  // Phase 107 — Profile imaging is `profile_photo_url` only. If we have a real
  // photo we render it; otherwise we fall through to a clean dual-initials
  // fallback. NO generated avatars (DiceBear / pravatar) — those were
  // deprecated in Phase 107 to eliminate broken-asset noise.
  const liveUrl = bestPhotoFor(student || employee || (photoUrl ? { profile_photo_url: photoUrl } : null));
  const url = liveUrl || photoUrl || null;
  const ini = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  return (<Avatar className={size}>{url && <AvatarImage src={url} alt={name} className="object-cover" />}<AvatarFallback className="bg-slate-100 text-slate-700 text-xs font-semibold">{ini}</AvatarFallback></Avatar>);
}

function PerformanceStars({ attendanceRate = 0, skillRating = 0 }) {
  const score = skillRating > 0 ? (attendanceRate * 0.5 + skillRating * 20 * 0.5) / 20 : attendanceRate / 20;
  const stars = Math.min(5, Math.max(0, Math.round(score)));
  return (<div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3.5 w-3.5 ${i <= stars ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20'}`} />)}</div>);
}

// Calendar component for viewing attendance
function AttendanceCalendar({ records, month, year }) {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const recordMap = {};
  (records || []).forEach(r => {
    const day = parseInt(r.date?.split('-')[2]);
    if (day) recordMap[day] = r.status;
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const statusColor = (s) => {
    if (s === 'present') return 'bg-emerald-500 text-white';
    if (s === 'absent') return 'bg-red-500 text-white';
    if (s === 'late') return 'bg-amber-500 text-white';
    if (s === 'leave') return 'bg-slate-400 text-white';
    return 'bg-muted text-muted-foreground';
  };

  const presentCount = Object.values(recordMap).filter(s => s === 'present').length;
  const absentCount = Object.values(recordMap).filter(s => s === 'absent').length;
  const lateCount = Object.values(recordMap).filter(s => s === 'late').length;
  const leaveCount = Object.values(recordMap).filter(s => s === 'leave').length;
  const total = presentCount + absentCount + lateCount + leaveCount;
  const rate = total > 0 ? Math.round(presentCount / total * 100) : 0;

  return (
    <div>
      {/* Stats row */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-emerald-500" /><span className="text-sm">Present: {presentCount}</span></div>
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-red-500" /><span className="text-sm">Absent: {absentCount}</span></div>
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-amber-500" /><span className="text-sm">Late: {lateCount}</span></div>
        <div className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-slate-400" /><span className="text-sm">Leave: {leaveCount}</span></div>
        <Badge variant={rate >= 80 ? 'default' : rate >= 60 ? 'secondary' : 'destructive'} className="text-sm tabular-nums">{rate}% Attendance</Badge>
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>)}
        {cells.map((day, i) => (
          <div key={i} className={`aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${day ? (recordMap[day] ? statusColor(recordMap[day]) : 'bg-muted/50 text-muted-foreground') : ''}`}>
            {day || ''}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Phase 307 — background geofence ping + live status badge
  const geofence = useGeofencePing({ role: user?.role });
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [progressCards, setProgressCards] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  const [attDate, setAttDate] = useState(new Date().toISOString().slice(0, 10));
  const [activeTab, setActiveTab] = useState('students');
  const [attSubTab, setAttSubTab] = useState('mark');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAddProgress, setShowAddProgress] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [progressForm, setProgressForm] = useState({ skills_rating: {}, comments: '', sport_override: '' });
  const [savingAtt, setSavingAtt] = useState(false);
  // Phase 103 — Injury eligibility map keyed by student_id → eligibility info
  const [injuryMap, setInjuryMap] = useState({});
  const [pendingPresentConfirm, setPendingPresentConfirm] = useState(null);
  // Phase 264 — Attendance Blitz Deck (single-card swipe interface)
  const [blitzActive, setBlitzActive] = useState(false);
  // Phase 296 — Today's training sessions (powers Generate-QR launcher)
  const [todaySessions, setTodaySessions] = useState([]);
  // View attendance state
  const [viewMonth, setViewMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewStudentId, setViewStudentId] = useState('');
  const [viewRecords, setViewRecords] = useState([]);
  const [loadingView, setLoadingView] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, studRes, notifRes, feesRes] = await Promise.all([
        api.get('/employee/dashboard'),
        api.get('/employee/students'),
        api.get('/employee/notifications'),
        api.get('/fees').catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data); setStudents(studRes.data); setNotifications(notifRes.data); setFees(feesRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchAttendance = useCallback(async () => {
    try {
      // Phase 80 — Canonical /attendance endpoint (same as Admin) for parity
      const res = await api.get('/attendance', { params: { date: attDate } });
      const map = {};
      res.data.forEach(r => { map[r.student_id] = r.status; });
      setAttendance(map);
    } catch (err) { console.error(err); }
  }, [attDate]);

  const fetchProgress = useCallback(async () => {
    try { const res = await api.get('/employee/progress-cards'); setProgressCards(res.data); }
    catch (err) { console.error(err); }
  }, []);

  const fetchViewAttendance = useCallback(async () => {
    if (!viewStudentId || !viewMonth) return;
    setLoadingView(true);
    try {
      // Phase 80 — Use the canonical /attendance endpoint with explicit student_id
      // filter so coach and admin see identical results (no client-side filtering needed).
      const res = await api.get('/attendance', { params: { month: viewMonth, student_id: viewStudentId } });
      setViewRecords(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingView(false); }
  }, [viewStudentId, viewMonth]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { if (activeTab === 'attendance' && attSubTab === 'mark') fetchAttendance(); }, [activeTab, attSubTab, attDate, fetchAttendance]);
  useEffect(() => { if (activeTab === 'progress') fetchProgress(); }, [activeTab, fetchProgress]);
  useEffect(() => { if (activeTab === 'attendance' && attSubTab === 'view') fetchViewAttendance(); }, [activeTab, attSubTab, viewStudentId, viewMonth, fetchViewAttendance]);

  // Phase 296 — Pull today's sessions to power the "Generate QR" CTA launcher.
  // We hit the canonical /training-sessions endpoint and filter to TODAY only
  // (`upcoming_only=true` would include tomorrow which the coach can't run QR
  // for right now). Silent failure — the launcher just hides if there's nothing.
  useEffect(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    api.get('/training-sessions', { params: { upcoming_only: true } })
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        setTodaySessions(all.filter((s) => s.date === todayStr));
      })
      .catch(() => setTodaySessions([]));
  }, []);

  // Phase 103 — Pull all active injuries once on mount; rebuild eligibility map per student
  useEffect(() => {
    api.get('/injuries', { params: { active_only: true } })
      .then(r => {
        const map = {};
        (r.data || []).forEach(inj => {
          const sid = inj.student_id;
          if (!sid) return;
          if (!map[sid]) map[sid] = { eligibility: 'eligible', injuries: [] };
          map[sid].injuries.push(inj);
          if (['reported', 'active', 'reinjured'].includes(inj.status)) map[sid].eligibility = 'not_eligible';
          else if (inj.status === 'recovering' && map[sid].eligibility !== 'not_eligible') map[sid].eligibility = 'limited_duty';
        });
        setInjuryMap(map);
      })
      .catch(() => {});
  }, []);

  const _doMarkAttendance = async (studentId, status) => {
    // Phase 102 — capture the previous status so the operator can Undo from the toast.
    const previousStatus = attendance[studentId];
    // Optimistic update for snappy pitch-side UX
    setAttendance(prev => ({ ...prev, [studentId]: status }));
    try {
      await api.post('/employee/attendance/mark', { student_id: studentId, date: attDate, status });
      // Refresh calendar view if currently looking at the same student
      if (viewStudentId === studentId && attSubTab === 'view') {
        fetchViewAttendance();
      }
      // Show a 4s undo toast — pitch-side coaches make rapid taps; one accidental
      // tap shouldn't require digging into a calendar to fix.
      const stu = students.find(s => s.id === studentId);
      const label = stu?.name ? stu.name.split(' ')[0] : 'student';
      toast.success(`${label} marked ${status}`, {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: async () => {
            // Revert: if there was no previous status, send the legacy "leave" sentinel?
            // Cleaner: re-write with the previous status, or unset entirely.
            try {
              if (previousStatus) {
                await api.post('/employee/attendance/mark', { student_id: studentId, date: attDate, status: previousStatus });
                setAttendance(prev => ({ ...prev, [studentId]: previousStatus }));
                toast.message(`Reverted to ${previousStatus}`);
              } else {
                // No prior status — best we can do is remove from local map. The
                // backend keeps the latest entry; a fresh tap will overwrite it.
                setAttendance(prev => { const next = { ...prev }; delete next[studentId]; return next; });
                toast.message('Cleared locally — re-mark to confirm');
              }
            } catch {
              toast.error('Failed to undo');
            }
          },
        },
      });
    } catch (err) {
      // Revert on failure
      setAttendance(prev => {
        const next = { ...prev };
        if (previousStatus) next[studentId] = previousStatus;
        else delete next[studentId];
        return next;
      });
      toast.error('Failed to mark attendance');
    }
  };

  // Phase 103 — Safety override: marking an injured student "present" requires
  // explicit operator confirmation. Other statuses (absent / late / leave) are
  // recorded directly because they don't risk on-field harm.
  const handleMarkAttendance = (studentId, status) => {
    if (status === 'present') {
      const info = injuryMap[studentId];
      if (info && info.eligibility !== 'eligible') {
        setPendingPresentConfirm({ studentId, info });
        return;
      }
    }
    return _doMarkAttendance(studentId, status);
  };

  const confirmInjuredPresent = async () => {
    if (!pendingPresentConfirm) return;
    const { studentId } = pendingPresentConfirm;
    setPendingPresentConfirm(null);
    await _doMarkAttendance(studentId, 'present');
  };

  const handleBulkPresent = async () => {
    setSavingAtt(true);
    // Phase 102 — snapshot previous statuses for Undo
    const previousMap = {};
    filteredStudents.forEach(s => { previousMap[s.id] = attendance[s.id]; });
    const records = filteredStudents.map(s => ({ student_id: s.id, status: 'present' }));
    try {
      await api.post('/employee/attendance/bulk', { date: attDate, records });
      const map = {};
      records.forEach(r => { map[r.student_id] = 'present'; });
      setAttendance(prev => ({ ...prev, ...map }));
      toast.success(`All ${records.length} students marked present`, {
        duration: 6000,
        action: {
          label: 'Undo all',
          onClick: async () => {
            try {
              // Revert each student to its prior status; for those with no prior,
              // we can't unset on backend so we re-emit the previous value or skip.
              const undoRecords = Object.entries(previousMap)
                .filter(([, v]) => v && v !== 'present')
                .map(([sid, st]) => ({ student_id: sid, status: st }));
              if (undoRecords.length > 0) {
                await api.post('/employee/attendance/bulk', { date: attDate, records: undoRecords });
              }
              setAttendance(prev => {
                const next = { ...prev };
                Object.entries(previousMap).forEach(([sid, st]) => {
                  if (st) next[sid] = st;
                  else delete next[sid];
                });
                return next;
              });
              toast.message('Bulk mark reverted');
            } catch {
              toast.error('Failed to undo bulk mark');
            }
          },
        },
      });
    } catch (err) { toast.error('Failed'); }
    finally { setSavingAtt(false); }
  };

  const handleAddProgress = async () => {
    if (!showAddProgress) return;
    try {
      await api.post('/employee/progress-cards', { student_id: showAddProgress.id, skills_rating: progressForm.skills_rating, comments: progressForm.comments });
      toast.success(`Progress card saved for ${showAddProgress.name}`);
      setShowAddProgress(null); setProgressForm({ skills_rating: {}, comments: '', sport_override: '' }); fetchProgress();
    } catch (err) { toast.error('Failed'); }
  };

  const batches = [...new Set(students.map(s => s.batch).filter(Boolean))];
  const filteredStudents = students.filter(s => {
    if (batchFilter !== 'all' && s.batch !== batchFilter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const initials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  const empInfo = dashboard?.employee;
  const stats = dashboard?.stats;
  const personal = dashboard?.personal_kpis || {};
  const presentCount = Object.values(attendance).filter(v => v === 'present').length;
  const viewMonthParts = viewMonth.split('-');
  const viewYear = parseInt(viewMonthParts[0]);
  const viewMon = parseInt(viewMonthParts[1]);
  const selectedStudentName = students.find(s => s.id === viewStudentId)?.name || '';

  if (loading) return (<div className="space-y-6" data-testid="coach-dashboard-loading"><Skeleton className="h-10 w-64" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-96 rounded-xl" /></div>);

  return (
    <div className="space-y-5" data-testid="coach-dashboard-page">
      {/* Personalized Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14 ring-2 ring-emerald-500/20">{(empInfo?.photo_url || bestPhotoFor(empInfo)) && <AvatarImage src={empInfo?.photo_url || bestPhotoFor(empInfo)} alt={user?.name} className="object-cover" />}<AvatarFallback className="bg-slate-100 text-slate-700 text-xl font-bold">{initials(user?.name || empInfo?.name)}</AvatarFallback></Avatar>
          <div>
            <p className="text-xs text-muted-foreground">Welcome back,</p>
            <h1 className="text-xl sm:text-2xl font-display font-bold">{user?.name || empInfo?.name}!</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-xs">{empInfo?.role || 'Coach'}</Badge>
              {empInfo?.department && <Badge variant="secondary" className="text-xs">{empInfo.department}</Badge>}
              {personal.batch_names?.length > 0 && <span className="text-xs text-muted-foreground">{personal.batch_names.join(', ')}</span>}
              {/* Phase 307 — live geofence clock-in badge */}
              <GeofenceStatusDot {...geofence} />
            </div>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="relative" onClick={() => setShowNotifications(true)} data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {notifications.filter(n => !n.read).length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>}
        </Button>
      </div>

      {/* Attendance Reminder */}
      {presentCount < (stats?.total_students || 0) && (
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/20"><AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-sm text-amber-700 dark:text-amber-400">Student attendance pending: {presentCount}/{stats?.total_students} marked today</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setActiveTab('attendance'); setAttSubTab('mark'); }}>Mark Now</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Phase 220 — Coach onboarding walkthrough (silent if SA hasn't pinned any) */}
      <SelfServiceWalkthroughSection placement="dashboard" />

      {/* Phase 313 — Live RSVP roster: 🟢/🔴/⚪ counts per today's session.
          Self-hides if there are zero sessions today OR the fetch errors. */}
      <LiveRsvpRoster />

      {/* Phase 296 — Today's Sessions: Generate-QR launcher for parent self-check-in. */}
      {todaySessions.length > 0 && (
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/20 dark:to-transparent" data-testid="coach-qr-launcher-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm flex items-center gap-1.5"><QrCode className="h-4 w-4 text-indigo-600" /> Today&apos;s Sessions</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Generate a 30s rotating QR — parents scan to check their kids in instantly.</p>
              </div>
              <Badge variant="secondary" className="text-xs shrink-0">{todaySessions.length} today</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {todaySessions.slice(0, 6).map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-white dark:bg-slate-900 px-3 py-2" data-testid={`coach-qr-session-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{s.title || s.batch || 'Session'}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {s.start_time || ''}{s.duration_minutes ? ` · ${s.duration_minutes}m` : ''}
                      {s.batch ? ` · ${s.batch}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                    onClick={() => navigate(`/coach/sessions/${s.id}/qr-scanner`)}
                    data-testid={`coach-qr-launch-btn-${s.id}`}
                  >
                    <QrCode className="h-3.5 w-3.5 mr-1" /> Generate QR
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Personal KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">My Attendance</span><CalendarCheck className="h-4 w-4 text-emerald-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-emerald-600">{personal.my_attendance_rate || 100}%</p>
            <p className="text-xs text-muted-foreground">{personal.my_sessions_this_month || 0} sessions this month</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-500" onClick={() => setActiveTab('students')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Assigned Batches</span><Users className="h-4 w-4 text-amber-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-amber-600">{personal.assigned_batches || 0}</p>
            <p className="text-xs text-muted-foreground">{stats?.total_students || 0} students total</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Pending Leaves</span><CalendarDays className="h-4 w-4 text-red-500" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-red-500">{personal.pending_leaves || 0}</p>
            <p className="text-xs text-muted-foreground">CL: {personal.leave_balance?.CL || 0} | SL: {personal.leave_balance?.SL || 0} | PL: {personal.leave_balance?.PL || 0}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500" onClick={() => setActiveTab('progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Progress Cards</span><TrendingUp className="h-4 w-4 text-blue-600" /></div>
            <p className="text-2xl font-display font-bold tabular-nums text-blue-600">{stats?.progress_cards_created || 0}</p>
            <p className="text-xs text-muted-foreground">Created by you</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs: Students | Attendance | Progress */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto flex">
          <TabsTrigger value="students" className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="tab-my-students"><Users className="h-3.5 w-3.5 mr-1 hidden sm:inline" /> Classroom</TabsTrigger>
          <TabsTrigger value="attendance" className="flex-1 sm:flex-none text-xs sm:text-sm"><ClipboardCheck className="h-3.5 w-3.5 mr-1 hidden sm:inline" /> Attendance</TabsTrigger>
          <TabsTrigger value="progress" className="flex-1 sm:flex-none text-xs sm:text-sm"><TrendingUp className="h-3.5 w-3.5 mr-1 hidden sm:inline" /> Progress</TabsTrigger>
        </TabsList>

        {/* ===== STUDENTS TAB ===== */}
        <TabsContent value="students">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm">Student Oversight & Performance <Badge variant="secondary" className="ml-1">{filteredStudents.length}</Badge></CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 sm:w-44"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs" /></div>
                  <Select value={batchFilter} onValueChange={setBatchFilter}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Batch" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{batches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="sm:hidden divide-y">{filteredStudents.map(s => (
                <div key={s.id} className="flex items-center gap-3 p-3">
                  <ProfilePic name={s.name} student={s} type="student" size="h-10 w-10" />
                  <div className="flex-1 min-w-0"><p className="font-medium text-sm">{s.name}</p><div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{s.batch || '-'}</Badge><span className="text-xs text-muted-foreground">{s.attendance_rate || 0}% att</span></div></div>
                  <div className="flex gap-1"><Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setSelectedStudent(s)}><Users className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setShowAddProgress(s); setProgressForm({ skills_rating: {}, comments: '', sport_override: '' }); }}><Plus className="h-4 w-4" /></Button></div>
                </div>
              ))}</div>
              <div className="hidden sm:block overflow-x-auto">
                <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Batch</TableHead><TableHead>Course</TableHead><TableHead>Attendance</TableHead><TableHead>Fee Status</TableHead><TableHead>Performance</TableHead><TableHead className="w-24">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>{filteredStudents.map(s => {
                    // Calculate fee status for this student
                    const studentFees = fees.filter(f => f.student_id === s.id);
                    const pendingFees = studentFees.filter(f => f.status === 'pending' || f.status === 'overdue');
                    const feeStatus = pendingFees.length > 0 ? 'pending' : studentFees.length > 0 ? 'paid' : 'none';
                    
                    return (
                    <TableRow key={s.id} className="group">
                      <TableCell><div className="flex items-center gap-2"><ProfilePic name={s.name} student={s} type="student" size="h-7 w-7" /><div><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-muted-foreground">{s.phone || ''}</p></div></div></TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{s.batch || '-'}</Badge></TableCell>
                      <TableCell className="text-sm">{s.course || s.sport || '-'}</TableCell>
                      <TableCell><div className="flex items-center gap-2"><Progress value={s.attendance_rate || 0} className="h-1.5 w-14" /><span className="text-xs font-medium tabular-nums">{s.attendance_rate || 0}%</span></div></TableCell>
                      <TableCell>
                        {feeStatus === 'pending' && (
                          <Badge className="text-xs bg-red-100 text-red-700 border-red-200">Pending</Badge>
                        )}
                        {feeStatus === 'paid' && (
                          <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Paid</Badge>
                        )}
                        {feeStatus === 'none' && (
                          <Badge variant="outline" className="text-xs">No Fees</Badge>
                        )}
                      </TableCell>
                      <TableCell><PerformanceStars attendanceRate={s.attendance_rate || 0} /></TableCell>
                      <TableCell><div className="flex gap-1"><Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSelectedStudent(s)}>Profile</Button><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setShowAddProgress(s); setProgressForm({ skills_rating: {}, comments: '', sport_override: '' }); }}><Plus className="h-3 w-3" /></Button></div></TableCell>
                    </TableRow>
                  )})}</TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== ATTENDANCE TAB (with sub-tabs) ===== */}
        <TabsContent value="attendance">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Button variant={attSubTab === 'mark' ? 'default' : 'outline'} size="sm" onClick={() => setAttSubTab('mark')} data-testid="att-sub-mark">
                  <ClipboardCheck className="h-4 w-4 mr-1" /> Mark Attendance
                </Button>
                <Button variant={attSubTab === 'view' ? 'default' : 'outline'} size="sm" onClick={() => setAttSubTab('view')} data-testid="att-sub-view">
                  <Eye className="h-4 w-4 mr-1" /> View Attendance
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* MARK ATTENDANCE SUB-TAB */}
              {attSubTab === 'mark' && (
                <div className="space-y-3">
                  {/* Phase 296 — Inline QR launcher row (mirrors the dashboard launcher
                      so coaches who pin themselves to the Attendance tab still discover QR). */}
                  {todaySessions.length > 0 && (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 px-3 py-2 flex-wrap" data-testid="coach-qr-attendance-launcher">
                      <div className="flex items-center gap-2 min-w-0">
                        <QrCode className="h-4 w-4 text-indigo-600 shrink-0" />
                        <p className="text-xs text-indigo-900 dark:text-indigo-200">
                          <span className="font-semibold">QR Self Check-in</span> · let parents scan in their kids — auto-rotates every 30s.
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {todaySessions.slice(0, 3).map((s) => (
                          <Button
                            key={s.id}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                            onClick={() => navigate(`/coach/sessions/${s.id}/qr-scanner`)}
                            data-testid={`coach-qr-attendance-btn-${s.id}`}
                          >
                            <QrCode className="h-3 w-3 mr-1" /> {s.start_time || s.batch || 'Session'}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div><p className="text-sm font-medium">Mark Attendance</p><p className="text-xs text-muted-foreground">{presentCount} present / {filteredStudents.length} total</p></div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input type="date" value={attDate} onChange={(e) => setAttDate(e.target.value)} className="w-auto h-8 text-xs" />
                      <Select value={batchFilter} onValueChange={setBatchFilter}><SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Batch" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{batches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
                      <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={handleBulkPresent} disabled={savingAtt}><CheckCircle className="h-3.5 w-3.5 mr-1" /> All Present</Button>
                      {/* Phase 264 — One-tap "Blitz" mode for high-tempo sessions */}
                      <Button
                        size="sm"
                        variant={blitzActive ? 'default' : 'outline'}
                        className={`h-8 text-xs ${blitzActive ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
                        onClick={() => setBlitzActive((v) => !v)}
                        disabled={filteredStudents.length === 0}
                        data-testid="att-blitz-toggle"
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" /> {blitzActive ? 'Exit Blitz' : 'Blitz'}
                      </Button>
                    </div>
                  </div>
                  <Progress value={filteredStudents.length > 0 ? (presentCount / filteredStudents.length * 100) : 0} className="h-2" />

                  {/* Phase 264 — Blitz mode: single-card swipe deck. Falls back
                      to the standard roster table when blitz is inactive. */}
                  {blitzActive ? (
                    <div className="py-4" data-testid="att-blitz-deck-wrapper">
                      <AttendanceBlitzDeck
                        students={filteredStudents}
                        date={attDate}
                        endpoint="/employee/attendance/bulk"
                        onComplete={() => { setBlitzActive(false); fetchAttendance(); }}
                        onClose={() => setBlitzActive(false)}
                      />
                      <p className="text-[10px] text-center text-muted-foreground mt-3">
                        Tip · One tap per student. Records auto-save at the end of the deck. Undo to revisit the last card.
                      </p>
                    </div>
                  ) : (
                    <>
                  <div className="sm:hidden divide-y border rounded-lg">{filteredStudents.map(s => (
                    <div key={s.id} className="flex items-center gap-3 p-3">
                      <ProfilePic name={s.name} student={s} type="student" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.batch || ''}</p></div>
                      <div className="flex gap-1">
                        <Button size="sm" variant={attendance[s.id] === 'present' ? 'default' : 'outline'} className="h-8 w-8 p-0" onClick={() => handleMarkAttendance(s.id, 'present')}><CheckCircle className="h-4 w-4" /></Button>
                        <Button size="sm" variant={attendance[s.id] === 'absent' ? 'destructive' : 'outline'} className="h-8 w-8 p-0" onClick={() => handleMarkAttendance(s.id, 'absent')}><XCircle className="h-4 w-4" /></Button>
                        <Button size="sm" variant={attendance[s.id] === 'late' ? 'secondary' : 'outline'} className="h-8 w-8 p-0" onClick={() => handleMarkAttendance(s.id, 'late')}><Clock className="h-4 w-4" /></Button>
                        <Button size="sm" variant={attendance[s.id] === 'leave' ? 'outline' : 'outline'} className={`h-8 w-8 p-0 ${attendance[s.id] === 'leave' ? 'bg-slate-200 text-slate-700' : ''}`} onClick={() => handleMarkAttendance(s.id, 'leave')} title="Leave"><Users className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}</div>
                  <div className="hidden sm:block">
                    <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Batch</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                      <TableBody>{filteredStudents.map(s => {
                        const inj = injuryMap[s.id];
                        return (
                        <TableRow key={s.id} className={attendance[s.id] === 'present' ? 'bg-emerald-50/50 dark:bg-emerald-950/10' : ''}>
                          <TableCell><div className="flex items-center gap-2"><ProfilePic name={s.name} student={s} type="student" size="h-7 w-7" /><span className="font-medium text-sm">{s.name}</span>{inj && (<Badge variant="outline" className={`text-[10px] gap-1 ${inj.eligibility === 'not_eligible' ? 'bg-rose-50 text-rose-700 border-rose-300' : 'bg-amber-50 text-amber-700 border-amber-300'}`} title={`Injury: ${(inj.injuries[0] || {}).injury_type} (${(inj.injuries[0] || {}).body_part})`} data-testid={`coach-inj-badge-${s.id}`}><AlertTriangle className="h-2.5 w-2.5" />{inj.eligibility === 'not_eligible' ? 'Not eligible' : 'Limited'}</Badge>)}</div></TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{s.batch || '-'}</Badge></TableCell>
                          <TableCell>{attendance[s.id] ? <Badge variant={attendance[s.id] === 'present' ? 'default' : attendance[s.id] === 'late' ? 'secondary' : attendance[s.id] === 'leave' ? 'outline' : 'destructive'} className={`text-xs capitalize ${attendance[s.id] === 'leave' ? 'bg-slate-100 text-slate-700' : ''}`}>{attendance[s.id]}</Badge> : <span className="text-xs text-muted-foreground">Not marked</span>}</TableCell>
                          <TableCell><div className="flex gap-1">
                            <Button size="sm" variant={attendance[s.id] === 'present' ? 'default' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(s.id, 'present')}><CheckCircle className="h-3 w-3 mr-0.5" /> P</Button>
                            <Button size="sm" variant={attendance[s.id] === 'absent' ? 'destructive' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(s.id, 'absent')}><XCircle className="h-3 w-3 mr-0.5" /> A</Button>
                            <Button size="sm" variant={attendance[s.id] === 'late' ? 'secondary' : 'outline'} className="h-7 text-xs px-2" onClick={() => handleMarkAttendance(s.id, 'late')}><Clock className="h-3 w-3 mr-0.5" /> L</Button>
                            <Button size="sm" variant="outline" className={`h-7 text-xs px-2 ${attendance[s.id] === 'leave' ? 'bg-slate-200 text-slate-700' : ''}`} onClick={() => handleMarkAttendance(s.id, 'leave')} title="Leave"><Users className="h-3 w-3 mr-0.5" /> Lv</Button>
                          </div></TableCell>
                        </TableRow>
                      );})}</TableBody>
                    </Table>
                  </div>
                    </>
                  )}
                </div>
              )}

              {/* VIEW ATTENDANCE SUB-TAB */}
              {attSubTab === 'view' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="space-y-1.5 flex-1 max-w-xs">
                      <Label className="text-xs font-medium">Select Student</Label>
                      <Select value={viewStudentId} onValueChange={setViewStudentId}>
                        <SelectTrigger className="h-9" data-testid="view-att-student"><SelectValue placeholder="Choose a student..." /></SelectTrigger>
                        <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} — {s.batch || 'No batch'}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Month</Label>
                      <Input type="month" value={viewMonth} onChange={(e) => setViewMonth(e.target.value)} className="w-auto h-9" data-testid="view-att-month" />
                    </div>
                  </div>

                  {!viewStudentId ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                      <CalendarDays className="h-12 w-12 mb-4 opacity-30" />
                      <p className="text-sm font-medium">Select a student to view attendance</p>
                      <p className="text-xs">Choose from the dropdown above</p>
                    </div>
                  ) : loadingView ? (
                    <div className="py-12 text-center"><Skeleton className="h-64 w-full rounded-xl" /></div>
                  ) : (
                    <div className="max-w-md mx-auto">
                      <div className="text-center mb-4">
                        <h3 className="text-lg font-display font-semibold">{selectedStudentName}</h3>
                        <p className="text-sm text-muted-foreground">{new Date(viewYear, viewMon - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                      </div>
                      <AttendanceCalendar records={viewRecords} month={viewMon} year={viewYear} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PROGRESS TAB (Phase 81 — Scout-Grade Search + Filters) ===== */}
        <TabsContent value="progress">
          <CoachProgressView
            progressCards={progressCards}
            students={students}
            onAddNew={(s) => { setShowAddProgress(s); setProgressForm({ skills_rating: {}, comments: '', sport_override: '' }); }}
          />
        </TabsContent>
      </Tabs>

      {/* Student Profile Modal */}
      <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Student Profile</DialogTitle></DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">{(selectedStudent.photo_url || bestPhotoFor(selectedStudent)) && <AvatarImage src={selectedStudent.photo_url || bestPhotoFor(selectedStudent)} alt={selectedStudent.name} className="object-cover" />}<AvatarFallback className="bg-slate-100 text-slate-700 text-xl font-bold">{initials(selectedStudent.name)}</AvatarFallback></Avatar>
                <div><h3 className="text-lg font-display font-bold">{selectedStudent.name}</h3><div className="flex items-center gap-2 mt-1"><Badge variant="outline" className="text-xs">{selectedStudent.batch || 'No batch'}</Badge><Badge variant="outline" className="text-xs">{selectedStudent.course || selectedStudent.sport || 'General'}</Badge></div></div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><h4 className="text-sm font-semibold">Contact</h4>{selectedStudent.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {selectedStudent.phone}</div>}{selectedStudent.email && <div className="flex items-center gap-2 text-sm"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> {selectedStudent.email}</div>}{selectedStudent.guardian_name && <div className="text-sm"><span className="text-muted-foreground">Guardian:</span> {selectedStudent.guardian_name}</div>}</div>
                <div className="space-y-2"><h4 className="text-sm font-semibold">Stats</h4><div className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-blue-600" /><span className="text-sm">{selectedStudent.attendance_rate || 0}% Attendance</span></div>{selectedStudent.bmi && <div className="flex items-center gap-2"><Award className="h-4 w-4 text-amber-600" /><PerformanceStars attendanceRate={selectedStudent.attendance_rate || 0} /></div>}{selectedStudent.height && <div className="text-sm text-muted-foreground">{selectedStudent.height}cm / {selectedStudent.weight}kg</div>}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={() => { setSelectedStudent(null); setShowAddProgress(selectedStudent); setProgressForm({ skills_rating: {}, comments: '', sport_override: '' }); }}><Plus className="h-4 w-4 mr-1" /> Add Progress</Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setSelectedStudent(null); setActiveTab('attendance'); setAttSubTab('view'); setViewStudentId(selectedStudent.id); }}><Eye className="h-4 w-4 mr-1" /> View Attendance</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Progress Modal */}
      <Dialog open={!!showAddProgress} onOpenChange={() => setShowAddProgress(null)}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><Award className="h-5 w-5 text-amber-600" /> Progress — {showAddProgress?.name}</DialogTitle></DialogHeader>
          {/* Phase 98 — Sport-aware skill matrix resolved from the student's enrolled
              sport. Hockey players get hockey metrics, cricketers get cricket metrics,
              and unknown sports fall back to a generic five-skill template. */}
          {(() => {
            // Phase 98.c — corrected lookup chain (sport → course → batch) plus
            // a manual override dropdown when neither tag resolves to a known sport.
            const resolvedSport = showAddProgress?.sport || showAddProgress?.course || '';
            const activeSport = progressForm.sport_override || resolvedSport;
            const matrix = getSportMatrix(activeSport);
            const isFallback = matrix.isFallback === true;
            return (
              <div className="space-y-4">
                {isFallback ? (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px]" data-testid="coach-progress-sport-fallback-banner">
                    <div className="flex items-start gap-1.5 text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-bold">Generic matrix · sport not tagged</p>
                        <p className="text-amber-700 text-[10px]">Pick a sport to use specialised metrics.</p>
                      </div>
                    </div>
                    <select
                      value={progressForm.sport_override}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, sport_override: e.target.value, skills_rating: {} }))}
                      className="bg-white border border-amber-300 text-slate-800 px-2 py-1 rounded-md font-bold text-[11px]"
                      data-testid="coach-progress-sport-override-select"
                    >
                      <option value="">Tag Student Sport →</option>
                      {AVAILABLE_SPORTS_LIST.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="rounded-md bg-primary/5 border border-primary/15 px-2.5 py-1.5 text-[11px] text-primary flex items-center justify-between" data-testid="coach-progress-sport-banner">
                    <span><span className="font-semibold">{matrix.sport}</span> evaluation matrix</span>
                    {progressForm.sport_override
                      ? <span className="opacity-60">manual override</span>
                      : <span className="opacity-60">from "{resolvedSport}"</span>}
                  </div>
                )}
                <div className="space-y-3">{matrix.skills.map(skill => (
                  <div key={skill.id} className="flex items-center justify-between" data-testid={`coach-progress-skill-row-${skill.id}`}>
                    <Label className="text-sm">{skill.label}</Label>
                    <div className="flex gap-1">{[1,2,3,4,5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setProgressForm(prev => ({ ...prev, skills_rating: { ...prev.skills_rating, [skill.id]: n } }))}
                        className="focus:outline-none"
                        data-testid={`coach-progress-star-${skill.id}-${n}`}
                      >
                        <Star className={`h-5 w-5 transition-colors ${(progressForm.skills_rating[skill.id] || 0) >= n ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20 hover:text-amber-300'}`} />
                      </button>
                    ))}</div>
                  </div>
                ))}</div>
                <div className="space-y-1.5"><Label className="text-sm">Coach Notes</Label><Textarea value={progressForm.comments} onChange={(e) => setProgressForm(prev => ({ ...prev, comments: e.target.value }))} placeholder="Performance notes..." rows={3} /></div>
                <Button className="w-full" onClick={handleAddProgress} data-testid="coach-progress-save"><CheckCircle className="h-4 w-4 mr-2" /> Save Progress Card</Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Phase 103 — Safety override: confirm marking an injured student Present */}
      <Dialog open={!!pendingPresentConfirm} onOpenChange={(v) => { if (!v) setPendingPresentConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="injury-safety-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              Field eligibility warning
            </DialogTitle>
            <DialogDescription className="text-xs">
              This player has an active injury on file. Confirm before marking Present.
            </DialogDescription>
          </DialogHeader>
          {pendingPresentConfirm && (
            <div className="space-y-3 text-sm">
              <p>
                <strong>{(students.find(s => s.id === pendingPresentConfirm.studentId) || {}).name}</strong> has{' '}
                {pendingPresentConfirm.info.eligibility === 'not_eligible' ? 'an active injury' : 'a recovering injury'}.
              </p>
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-1">
                {(pendingPresentConfirm.info.injuries || []).map((inj) => (
                  <p key={inj.id} className="text-xs text-rose-800">
                    <strong className="capitalize">{inj.injury_type}</strong> — {inj.body_part} ({inj.severity})
                    {inj.expected_recovery_date ? ` · expected recovery ${inj.expected_recovery_date}` : ''}
                  </p>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {pendingPresentConfirm.info.eligibility === 'not_eligible'
                  ? 'Marking Present overrides the not-eligible flag. Only do this if the injury record is out of date.'
                  : 'Marking Present on limited-duty status is OK if the coach plans modified drills only.'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingPresentConfirm(null)} data-testid="injury-safety-cancel">Cancel</Button>
            <Button onClick={confirmInjuredPresent} className="bg-rose-600 hover:bg-rose-700" data-testid="injury-safety-confirm">Mark Present anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Panel */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Notifications</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">{notifications.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No notifications</p> : notifications.map(n => (
            <div key={n.id} className={`p-3 rounded-lg border text-sm ${n.type === 'reminder' ? 'bg-amber-50 border-amber-200' : n.type === 'assignment' ? 'bg-blue-50 border-blue-200' : 'bg-muted/50'}`}>
              <p className="text-sm">{n.message}</p><p className="text-xs text-muted-foreground mt-0.5">{new Date(n.date).toLocaleDateString()}</p>
            </div>
          ))}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Phase 81 — Scout-Grade Progress search + filter view
function CoachProgressView({ progressCards, students, onAddNew }) {
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');     // all | gte4 | 3to4 | lt3
  const [monthFilter, setMonthFilter] = useState('all');       // all | YYYY-MM
  const [sortBy, setSortBy] = useState('newest');              // newest | rating-desc | rating-asc

  // Build batch options from current students
  const batches = Array.from(new Set(students.map(s => s.batch).filter(Boolean)));

  // Build month options from existing progress cards (last 12 months)
  const months = Array.from(new Set(
    progressCards.map(c => (c.created_at || '').slice(0, 7)).filter(Boolean)
  )).sort().reverse().slice(0, 12);

  const computeAvg = (skills_rating) => {
    const vals = Object.values(skills_rating || {});
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  };

  const cards = progressCards
    .map(c => {
      const student = students.find(s => s.id === c.student_id);
      return { card: c, student, avg: computeAvg(c.skills_rating) };
    })
    .filter(({ card, student, avg }) => {
      if (!student) return false;
      if (search) {
        const q = search.toLowerCase();
        const hit = (student.name || '').toLowerCase().includes(q)
          || (student.custom_id || '').toLowerCase().includes(q)
          || (student.id || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (batchFilter !== 'all' && (student.batch || '') !== batchFilter) return false;
      if (ratingFilter === 'gte4' && avg < 4) return false;
      if (ratingFilter === '3to4' && (avg < 3 || avg >= 4)) return false;
      if (ratingFilter === 'lt3' && avg >= 3) return false;
      if (monthFilter !== 'all' && (card.created_at || '').slice(0, 7) !== monthFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'rating-desc') return b.avg - a.avg;
      if (sortBy === 'rating-asc')  return a.avg - b.avg;
      return (b.card.created_at || '').localeCompare(a.card.created_at || '');
    });

  const clearAll = () => { setSearch(''); setBatchFilter('all'); setRatingFilter('all'); setMonthFilter('all'); };
  const hasFilters = search || batchFilter !== 'all' || ratingFilter !== 'all' || monthFilter !== 'all';

  return (
    <div className="space-y-3" data-testid="progress-tab">
      {/* === Filter Bar === */}
      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by student name or ID..."
              className="pl-8 h-8 text-xs"
              data-testid="progress-search"
            />
          </div>
          <Select value={batchFilter} onValueChange={setBatchFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-batch"><SelectValue placeholder="Batch" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All batches</SelectItem>
              {batches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ratingFilter} onValueChange={setRatingFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-rating"><SelectValue placeholder="Rating" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              <SelectItem value="gte4">≥ 4.0 ⭐</SelectItem>
              <SelectItem value="3to4">3.0 – 4.0</SelectItem>
              <SelectItem value="lt3">&lt; 3.0</SelectItem>
            </SelectContent>
          </Select>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="rating-desc">Top-rated first</SelectItem>
              <SelectItem value="rating-asc">Lowest-rated first</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearAll} data-testid="filter-clear">
              <XCircle className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
          <div className="flex-1" />
          <Select onValueChange={(v) => { const s = students.find(st => st.id === v); if (s) onAddNew(s); }}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="add-progress-select"><SelectValue placeholder="+ Add Progress Card..." /></SelectTrigger>
            <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Result count line */}
        <div className="flex items-center justify-between mt-2 px-1">
          <p className="text-[11px] text-muted-foreground" data-testid="progress-result-count">
            <span className="font-semibold text-foreground">{cards.length}</span> of {progressCards.length} progress card{progressCards.length === 1 ? '' : 's'}
          </p>
          {hasFilters && (
            <p className="text-[10px] text-muted-foreground">
              Filters: {[
                search && `"${search}"`,
                batchFilter !== 'all' && batchFilter,
                ratingFilter !== 'all' && ratingFilter,
                monthFilter !== 'all' && monthFilter,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </Card>

      {/* === Cards Grid === */}
      {progressCards.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Award className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Progress Cards Yet</h3>
            <p className="text-sm text-muted-foreground">Select a student to create the first card</p>
          </CardContent>
        </Card>
      ) : cards.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <h3 className="text-base font-semibold mb-1">No matches</h3>
            <p className="text-xs text-muted-foreground mb-3">Try different filters or clear them</p>
            <Button size="sm" variant="outline" onClick={clearAll}>Clear filters</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3" data-testid="progress-cards-grid">
          {cards.map(({ card, student }) => (
            <DigitalAthleteCard key={card.id} student={student} progressCard={card} />
          ))}
        </div>
      )}
    </div>
  );
}

